/**
 * QR 스티커 자동 출력 - USB 리더기 모드
 *
 * USB QR 리더기 입력 수신 → API 호출 → Brother QL 프린터 자동 출력
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { printQrSticker, getPrinterStatus, checkUsbBridge } from '../services/api';
import { extractQRCode } from '../utils/qrUtils';
import './QrStickerPage.css';

import { isBridgeApp } from '../utils/isBridgeApp';
const COOLDOWN_MS = 5000;
const MAX_LOGS = 5;
const PRINTER_POLL_INTERVAL = 30000;

export default function QrStickerReaderPage() {
  const navigate = useNavigate();
  const keyBufferRef = useRef('');
  const keyTimerRef = useRef(null);
  const [logs, setLogs] = useState([]);
  const [printing, setPrinting] = useState(false);
  const [printerStatus, setPrinterStatus] = useState(null);
  const [printerError, setPrinterError] = useState(null);
  const cooldownMap = useRef(new Map());
  const pollRef = useRef(null);
  const printingRef = useRef(false);

  // 프린터 상태 폴링 (USB는 backend에서 확인 불가 → 브릿지 앱에 직접 확인)
  const checkPrinter = useCallback(async () => {
    try {
      const res = await getPrinterStatus();
      const printer = res.printer;

      if (printer?.connection_type === 'usb') {
        // USB는 backend가 항상 'ready'를 반환하므로 bridge HTTP에 실제 상태 조회
        const bridge = await checkUsbBridge();
        setPrinterStatus({
          ...printer,
          reachable: bridge.connected,
          status: bridge.connected ? 'ready' : 'unreachable',
        });
        // USB 미연결은 풀스크린 에러 오버레이 안 띄움 (footer 빨강만)
        setPrinterError(null);
      } else {
        setPrinterStatus(printer);
        if (printer?.status === 'ready') {
          setPrinterError(null);
        } else if (printer?.error) {
          setPrinterError(printer.error);
        }
      }
    } catch {
      setPrinterStatus(null);
    }
  }, []);

  useEffect(() => {
    checkPrinter();
    pollRef.current = setInterval(checkPrinter, PRINTER_POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [checkPrinter]);

  const isCooldown = (qrCode) => {
    const last = cooldownMap.current.get(qrCode);
    if (last && Date.now() - last < COOLDOWN_MS) return true;
    return false;
  };

  const addLog = (success, message) => {
    const entry = {
      id: Date.now(),
      success,
      message,
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    };
    setLogs(prev => [entry, ...prev].slice(0, MAX_LOGS));
  };

  // USB QR 리더기: document keydown (hidden input 없이)
  const processQR = useCallback(async (rawValue) => {
    if (!rawValue || printingRef.current) return;

    const qrCode = extractQRCode(rawValue);
    if (!qrCode) {
      addLog(false, '유효하지 않은 QR 형식');
      return;
    }

    if (isCooldown(qrCode)) {
      addLog(false, '이미 출력된 QR입니다 (5초 대기)');
      return;
    }

    cooldownMap.current.set(qrCode, Date.now());
    setPrinting(true);
    printingRef.current = true;

    try {
      const result = await printQrSticker(qrCode);
      if (result?.no_printer) {
        addLog(false, result.message || 'USB 프린터 미연결');
      } else if (result?.usb_failed) {
        addLog(false, result.message || 'USB 출력 실패');
      } else {
        const countMsg = result.printed_count > 1 ? ` (${result.printed_count}장)` : '';
        addLog(true, `${result.label} - 출력 완료!${countMsg}`);
      }
    } catch (err) {
      const msg = err.message || '출력 실패';
      addLog(false, msg);
      if (err.error_code?.startsWith('PRINTER_')) {
        setPrinterError(msg);
        checkPrinter();
      }
    } finally {
      setPrinting(false);
      printingRef.current = false;
    }
  }, [checkPrinter]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const code = keyBufferRef.current.trim();
        if (code.length > 3) processQR(code);
        keyBufferRef.current = '';
        if (keyTimerRef.current) clearTimeout(keyTimerRef.current);
        return;
      }
      if (e.key.length === 1) {
        keyBufferRef.current += e.key;
        if (keyTimerRef.current) clearTimeout(keyTimerRef.current);
        keyTimerRef.current = setTimeout(() => { keyBufferRef.current = ''; }, 200);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [processQR]);

  const toggleFullscreen = () => {
    // 웹(크롬)에서는 전체화면으로 가지 않는다 — 태블릿 앱(ZlzlKiosk)에서만 (2026-08-06 요구).
    if (!isBridgeApp()) return;
    const el = document.documentElement;
    (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
  };

  return (
    <div className="qr-sticker-page">
      {/* 헤더 */}
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate('/')}>← 메뉴</button>
        <h1 className="page-header-title" onClick={toggleFullscreen}>⚡ QR스티커(자동)</h1>
        <span className="header-store-name">
          {localStorage.getItem('store_name') || ''}
        </span>
      </header>

      {/* 프린터 에러 전체 화면 */}
      {printerError && (
        <div className="printer-error-overlay">
          <div className="printer-error-card">
            <span className="printer-error-icon">🔴</span>
            <h2>프린터 오류</h2>
            <p>{printerError}</p>
            <p className="printer-error-sub">자동 복구 확인 중...</p>
          </div>
        </div>
      )}

      {/* 메인 영역 */}
      <main className="reader-content">
        <div className="reader-card">
          <span className="reader-card-icon">🏷️</span>
          <h2 className="reader-card-title">QR 스티커 자동 출력</h2>
          <p className="reader-card-subtitle">QR 리더기에 코드를 갖다대주세요</p>
          <div className={`reader-card-status ${printing ? 'is-printing' : ''}`}>
            {printing ? '🖨️ 출력 중...' : '⏳ 대기 중'}
          </div>
        </div>

        {/* 출력 로그 */}
        {logs.length > 0 && (
          <div className="reader-logs">
            {logs.map(log => (
              <div key={log.id} className={`print-log ${log.success ? 'success' : 'error'}`}>
                <span className="log-icon">{log.success ? '✅' : '❌'}</span>
                <span className="log-message">{log.message}</span>
                <span className="log-time">{log.time}</span>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* 프린터 상태 바 */}
      <footer className="printer-status-bar">
        {printerStatus?.status === 'ready' && (
          <span>🟢 {printerStatus.connection_type === 'usb' ? '🔌 USB' : (printerStatus.name || '프린터')} 연결됨 ({printerStatus.model})</span>
        )}
        {printerStatus?.status === 'unreachable' && (
          <span>🔴 {printerStatus.connection_type === 'usb' ? 'USB 프린터 미연결' : '프린터 연결 안 됨'}</span>
        )}
        {printerStatus?.status === 'disabled' && (
          <span>⚪ 프린터 비활성화</span>
        )}
        {!printerStatus && <span>⏳ 프린터 상태 확인 중...</span>}
      </footer>
    </div>
  );
}
